import posthog from 'posthog-js'
import { isAxiosError } from 'axios'
import { appVersion } from '@/appVersion'

// Treat the token as present only if it was actually injected. In deployed
// environments `entrypoint.sh` substitutes `__VITE_*__` placeholders in
// config.js; if the env var is missing the literal placeholder remains. Locally
// the token is read from .env (commented out by default). In both of those cases
// PostHog must stay completely off — no init, no network requests.
const token = globalThis.appConfig.VITE_POSTHOG_PROJECT_TOKEN

export const posthogEnabled = !!token && !token.startsWith('__VITE_')

// Browser noise that says nothing about a bug in our code
const IGNORED_ERROR_MESSAGES = [/^ResizeObserver loop/]

// The Ivion viewer runs this whole app again inside an iframe (/instance/...),
// so errors from NavVis' code are reported from there
const inIvionIframe = globalThis.self !== globalThis.top

const NETWORK_ERROR_MESSAGES = /network error|failed to fetch|load failed|networkerror/i

// A lazy-loaded chunk that no longer exists, typically because a deploy
// happened while the tab was open. Not a code bug, so it gets its own group
const CHUNK_LOAD_ERROR_MESSAGES =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css/i

const MAX_RESPONSE_MESSAGE_LENGTH = 300

// The same error can reach several handlers (e.g. router.onError and then
// unhandledrejection); report each one only once
const capturedErrors = new WeakSet<object>()

export function initPosthog() {
  if (!posthogEnabled) return

  posthog.init(token, {
    api_host: globalThis.appConfig.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com',
    defaults: '2026-01-30',
    capture_performance: true,
    // Our own handlers in main.ts report uncaught errors with extra context;
    // leaving the SDK's autocapture on (it follows the project setting when
    // unset) would report every error a second time
    capture_exceptions: false,
    opt_out_capturing_by_default: true
  })

  // Tag every event with the environment so prod/staging/dev can be told apart
  // even when they share a project. Falls back to 'unknown' if not injected.
  const rawEnv = globalThis.appConfig.VITE_ENVIRONMENT
  const environment = rawEnv && !rawEnv.startsWith('__VITE_') ? rawEnv : 'unknown'
  // The build identifiers let an error be tied to the deploy it came from
  posthog.register({
    environment,
    app_version: appVersion(),
    app_build_time: import.meta.env.VITE_BUILD_TIME,
    app_commit: import.meta.env.VITE_COMMIT_SHA || undefined,
    in_ivion_iframe: inIvionIframe
  })
}

/**
 * Sends an error to PostHog error tracking with as much context as we can
 * derive from it. Non-Error values (thrown strings, rejected objects) are
 * wrapped so they still get a stack, axios errors get the failed request
 * attached, and network failures get the browser's connection state.
 */
export function captureError(error: unknown, properties: Record<string, unknown> = {}) {
  if (!posthogEnabled) return

  if (typeof error === 'object' && error !== null) {
    if (capturedErrors.has(error)) return
    capturedErrors.add(error)
  }

  const { error: normalized, originalValue } = toError(error)
  if (IGNORED_ERROR_MESSAGES.some((pattern) => pattern.test(normalized.message))) return
  if (isIvionInterruptedDownload(normalized)) return

  posthog.captureException(normalized, {
    error_kind: errorKind(error, normalized),
    ...properties,
    ...(originalValue === undefined ? {} : { original_value: originalValue }),
    ...httpContext(error),
    ...networkContext(error, normalized),
    ...chunkLoadContext(normalized)
  })
}

function errorKind(original: unknown, error: Error): string {
  if (CHUNK_LOAD_ERROR_MESSAGES.test(error.message)) return 'chunk_load'
  if (isNetworkError(original, error)) return 'network'
  if (isAxiosError(original)) return 'http'
  return 'runtime'
}

// Chrome's `TypeError: network error` when one of Ivion's downloads is cut off,
// almost always in an idle tab (sleep, lost Wi-Fi/VPN). The browser creates it,
// so it has no stack frames and nothing we can act on (see ZDB-7228)
function isIvionInterruptedDownload(error: Error): boolean {
  return inIvionIframe && error.message === 'network error' && !hasStackFrames(error)
}

// V8 frames look like `    at fn (url)`, Firefox/Safari ones like `fn@url`
function hasStackFrames(error: Error): boolean {
  return (error.stack ?? '')
    .split('\n')
    .some((line) => line.trimStart().startsWith('at ') || line.includes('@'))
}

function toError(value: unknown): { error: Error; originalValue?: string } {
  if (value instanceof Error) return { error: value }
  if (typeof value === 'string') return { error: new Error(value) }

  let originalValue: string
  try {
    originalValue = JSON.stringify(value)
  } catch {
    originalValue = String(value)
  }
  return { error: new Error(`Non-Error value thrown: ${originalValue}`), originalValue }
}

function httpContext(error: unknown): Record<string, unknown> {
  if (!isAxiosError(error)) return {}

  const { config, response, code } = error
  const method = config?.method?.toUpperCase()
  const url = sanitizeUrl(config?.url, config?.baseURL)
  return {
    http_method: method,
    http_url: url,
    http_status: response?.status,
    http_error_code: code,
    http_response_message: responseMessage(response?.data),
    // Every axios error has the same message and a near-identical stack, so
    // without this they all collapse into one issue. Group per endpoint
    // and status instead, with ids stripped so one endpoint is one group
    ...(url && { $exception_fingerprint: [method, templateUrl(url), response?.status ?? code] })
  }
}

// Spring's error body ({ message, error, ... }) or a plain-text response
function responseMessage(data: unknown): string | undefined {
  let message: unknown
  if (typeof data === 'string') message = data
  else if (data && typeof data === 'object') {
    message = (data as Record<string, unknown>).message ?? (data as Record<string, unknown>).error
  }
  if (typeof message !== 'string' || !message) return undefined
  return message.slice(0, MAX_RESPONSE_MESSAGE_LENGTH)
}

// Replaces path segments that are ids (UUIDs, numbers) with `:id`
function templateUrl(url: string): string {
  return url
    .split('/')
    .map((segment) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment) ||
      /^\d+$/.test(segment)
        ? ':id'
        : segment
    )
    .join('/')
}

// Only origin + path: query strings can carry tokens (e.g. Ivion's
// `x_authorization`), and request headers are never sent at all.
function sanitizeUrl(url?: string, baseURL?: string): string | undefined {
  if (!url) return undefined
  try {
    const parsed = new URL(url, baseURL || globalThis.location.origin)
    return parsed.origin + parsed.pathname
  } catch {
    return url.split(/[?#]/)[0]
  }
}

function isNetworkError(original: unknown, error: Error): boolean {
  return (
    (isAxiosError(original) && original.code === 'ERR_NETWORK') ||
    NETWORK_ERROR_MESSAGES.test(error.message)
  )
}

function networkContext(original: unknown, error: Error): Record<string, unknown> {
  if (!isNetworkError(original, error)) return {}

  const connection = (navigator as any).connection
  return {
    network_online: navigator.onLine,
    network_effective_type: connection?.effectiveType,
    network_downlink: connection?.downlink
  }
}

function chunkLoadContext(error: Error): Record<string, unknown> {
  if (!CHUNK_LOAD_ERROR_MESSAGES.test(error.message)) return {}
  return { $exception_fingerprint: ['chunk_load_error'] }
}

export default posthog
